import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DriverAvatar, { SHOP_GLYPH } from './DriverAvatar';
import { AVATAR_ACCEPT, getAvatarFileError, isHeicFile, removeAvatar, uploadAvatar } from '../utils/avatarApi';

const SUCCESS_MESSAGE_MS = 4000;

// Wording for the driver's profile picture (default) and the shop portal's shop photo.
export const DRIVER_PHOTO_COPY = {
    noun: 'profile picture',
    title: 'Profile picture',
    newTitle: 'New profile picture',
    caption: 'Your photo appears on your CarFido profile and in the sidebar.',
    addLabel: 'Add profile picture',
    changeLabel: 'Change profile picture',
    updated: 'Profile picture updated.',
    removed: 'Profile picture removed.',
    uploadError: 'Unable to upload your profile picture.',
    removeError: 'Unable to remove your profile picture.',
};

export const SHOP_PHOTO_COPY = {
    noun: 'shop photo',
    title: 'Shop photo',
    newTitle: 'New shop photo',
    caption: 'Your shop photo or logo appears in the shop portal sidebar and settings.',
    addLabel: 'Add shop photo',
    changeLabel: 'Change shop photo',
    updated: 'Shop photo updated.',
    removed: 'Shop photo removed.',
    uploadError: 'Unable to upload the shop photo.',
    removeError: 'Unable to remove the shop photo.',
    glyph: SHOP_GLYPH,
};

// State for a Settings photo (driver profile picture or shop photo). Clicking the avatar
// opens a dialog (the Google / Instagram pattern): upload -> preview -> Save, or remove
// the current photo. `upload` / `remove` default to the driver endpoints.
export function useProfilePhotoEditor({
    avatarUrl,
    onAvatarChange,
    upload = uploadAvatar,
    remove = removeAvatar,
    copy = DRIVER_PHOTO_COPY,
}) {
    const fileInputRef = useRef(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    // Most non-Safari browsers cannot render HEIC; the server converts it to JPEG on save.
    const [previewUnavailable, setPreviewUnavailable] = useState(false);
    const [busy, setBusy] = useState(null); // null | 'saving' | 'removing'
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    useEffect(() => {
        if (!success) return undefined;
        const timer = setTimeout(() => setSuccess(''), SUCCESS_MESSAGE_MS);
        return () => clearTimeout(timer);
    }, [success]);

    const clearPending = () => {
        setPendingFile(null);
        setPreviewUrl('');
        setPreviewUnavailable(false);
    };

    const openDialog = useCallback(() => {
        setError('');
        setSuccess('');
        setDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        if (busy) return;
        clearPending();
        setError('');
        setDialogOpen(false);
    }, [busy]);

    const openPicker = useCallback(() => {
        if (busy) return;
        if (fileInputRef.current) fileInputRef.current.click();
    }, [busy]);

    const handleFileSelected = useCallback((event) => {
        const file = event.target.files && event.target.files[0];
        // Reset so picking the same file again still fires onChange.
        event.target.value = '';
        if (!file) return;
        const fileError = getAvatarFileError(file);
        if (fileError) {
            setError(fileError);
            return;
        }
        setError('');
        setPendingFile(file);
        setPreviewUnavailable(false);
        setPreviewUrl(URL.createObjectURL(file));
    }, []);

    const savePhoto = useCallback(async () => {
        if (!pendingFile || busy) return;
        setBusy('saving');
        setError('');
        try {
            const nextUrl = await upload(pendingFile);
            onAvatarChange(nextUrl);
            clearPending();
            setDialogOpen(false);
            setSuccess(copy.updated);
        } catch (err) {
            setError(err.message || copy.uploadError);
        } finally {
            setBusy(null);
        }
    }, [busy, copy, onAvatarChange, pendingFile, upload]);

    const removePhoto = useCallback(async () => {
        if (busy) return;
        setBusy('removing');
        setError('');
        try {
            await remove();
            onAvatarChange('');
            setDialogOpen(false);
            setSuccess(copy.removed);
        } catch (err) {
            setError(err.message || copy.removeError);
        } finally {
            setBusy(null);
        }
    }, [busy, copy, onAvatarChange, remove]);

    return {
        copy,
        fileInputRef,
        avatarUrl,
        previewUrl,
        hasPhoto: Boolean(avatarUrl),
        isPreviewing: Boolean(pendingFile),
        pendingIsHeic: isHeicFile(pendingFile),
        previewUnavailable,
        markPreviewUnavailable: () => setPreviewUnavailable(true),
        dialogOpen,
        busy,
        error,
        success,
        openDialog,
        closeDialog,
        openPicker,
        handleFileSelected,
        discardPreview: () => { if (!busy) { clearPending(); setError(''); } },
        savePhoto,
        removePhoto,
    };
}

const Icon = ({ children, size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
    </svg>
);

const CameraIcon = ({ size }) => (
    <Icon size={size}>
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
    </Icon>
);

const UploadIcon = () => (
    <Icon>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <path d="m17 8-5-5-5 5" />
        <path d="M12 3v12" />
    </Icon>
);

const TrashIcon = () => (
    <Icon>
        <path d="M3 6h18" />
        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </Icon>
);

const Spinner = ({ className = 'h-4 w-4 border-white/40 border-t-white' }) => (
    <span className={`animate-spin rounded-full border-2 ${className}`} aria-hidden="true" />
);

// Instagram-style full-width rows separated by hairlines.
const rowClass = 'flex min-h-[52px] w-full items-center justify-center gap-2.5 px-5 text-sm font-black transition disabled:cursor-default disabled:opacity-50';

const ProfilePhotoDialog = ({ editor, returnFocusRef }) => {
    const firstActionRef = useRef(null);
    const { closeDialog } = editor;

    useEffect(() => {
        if (firstActionRef.current) firstActionRef.current.focus();
    }, [editor.isPreviewing]);

    useEffect(() => {
        const handleKey = (event) => {
            if (event.key === 'Escape') closeDialog();
        };
        window.addEventListener('keydown', handleKey);
        const returnTo = returnFocusRef.current;
        return () => {
            window.removeEventListener('keydown', handleKey);
            if (returnTo) returnTo.focus();
        };
    }, [closeDialog, returnFocusRef]);

    const { copy } = editor;
    const shownUrl = editor.previewUrl || editor.avatarUrl;
    const title = editor.isPreviewing ? copy.newTitle : copy.title;
    let caption = copy.caption;
    if (editor.isPreviewing) {
        caption = editor.previewUnavailable
            ? `${editor.pendingIsHeic ? 'HEIC photos' : 'This photo'} can't be previewed in this browser. Save to see it.`
            : 'This is how your photo will look. It will be cropped to a circle.';
    }

    return createPortal(
        <div className="animate-dash-overlay-in fixed inset-0 z-[120] flex items-end justify-center bg-black/45 backdrop-blur-sm sm:items-center sm:px-4 sm:py-6">
            <div className="absolute inset-0" onMouseDown={closeDialog} aria-hidden="true" />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="profile-photo-dialog-title"
                aria-describedby="profile-photo-dialog-caption"
                className="animate-dash-modal-in relative z-[121] max-h-[92dvh] w-full overflow-y-auto overscroll-contain rounded-t-[26px] border border-[#d9d9d9] bg-white shadow-[0_30px_90px_rgba(20,20,20,0.28)] sm:max-w-sm sm:rounded-[26px]"
            >
                <div className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-[#d9d9d9] sm:hidden" aria-hidden="true" />
                <div className="flex items-center justify-between px-5 pb-1 pt-3 sm:pt-4">
                    <h2 id="profile-photo-dialog-title" className="text-lg font-black text-[#414141]">{title}</h2>
                    <button
                        type="button"
                        onClick={closeDialog}
                        disabled={Boolean(editor.busy)}
                        className="flex h-9 w-9 items-center justify-center rounded-full text-[#414141] transition hover:bg-[#f8f8f8] hover:text-[#414141] disabled:opacity-50"
                        aria-label="Close"
                    >
                        <Icon size={16}>
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </Icon>
                    </button>
                </div>

                <div className="flex flex-col items-center px-6 pb-5 pt-2 text-center">
                    <DriverAvatar
                        src={shownUrl}
                        alt={editor.previewUrl ? `New ${copy.noun} preview` : `Current ${copy.noun}`}
                        glyph={copy.glyph}
                        className={`h-32 w-32 sm:h-36 sm:w-36 ${editor.isPreviewing ? 'ring-4 ring-[#F5C2C4]' : ''}`}
                        iconSize={52}
                        strokeWidth={1.8}
                        onLoadError={editor.previewUrl ? editor.markPreviewUnavailable : undefined}
                    >
                        {editor.busy && (
                            <span className="absolute inset-0 flex items-center justify-center bg-black/35">
                                <Spinner className="h-8 w-8 border-[3px] border-white/40 border-t-white" />
                            </span>
                        )}
                    </DriverAvatar>
                    <p id="profile-photo-dialog-caption" className="mt-4 max-w-[260px] text-xs font-semibold leading-relaxed text-[#414141]">
                        {caption}
                    </p>
                    {editor.error && (
                        <p role="alert" className="mt-3 w-full rounded-xl bg-red-50 px-3 py-2 text-xs font-bold text-red-600">{editor.error}</p>
                    )}
                </div>

                {editor.isPreviewing ? (
                    <div className="border-t border-[#d9d9d9]">
                        <div className="px-5 py-4">
                            <button
                                ref={firstActionRef}
                                type="button"
                                onClick={editor.savePhoto}
                                disabled={Boolean(editor.busy)}
                                className="inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-full bg-[#D81F26] px-5 text-sm font-black text-white shadow-sm transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-70"
                            >
                                {editor.busy === 'saving' && <Spinner />}
                                {editor.busy === 'saving' ? 'Saving...' : 'Save photo'}
                            </button>
                        </div>
                        <div className="divide-y divide-[#d9d9d9] border-t border-[#d9d9d9]">
                            <button type="button" onClick={editor.openPicker} disabled={Boolean(editor.busy)} className={`${rowClass} text-[#414141] hover:bg-[#f8f8f8]`}>
                                Choose another photo
                            </button>
                            <button type="button" onClick={editor.discardPreview} disabled={Boolean(editor.busy)} className={`${rowClass} text-[#414141] hover:bg-[#F9F9F9]`}>
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="divide-y divide-[#d9d9d9] border-t border-[#d9d9d9]">
                        <button
                            ref={firstActionRef}
                            type="button"
                            onClick={editor.openPicker}
                            disabled={Boolean(editor.busy)}
                            className={`${rowClass} text-[#D81F26] hover:bg-[#f8f8f8]`}
                        >
                            <UploadIcon />
                            {editor.hasPhoto ? 'Upload new photo' : 'Upload photo'}
                        </button>
                        {editor.hasPhoto && (
                            <button
                                type="button"
                                onClick={editor.removePhoto}
                                disabled={Boolean(editor.busy)}
                                className={`${rowClass} text-red-600 hover:bg-red-50`}
                            >
                                {editor.busy === 'removing' ? <Spinner className="h-4 w-4 border-red-200 border-t-red-600" /> : <TrashIcon />}
                                {editor.busy === 'removing' ? 'Removing...' : 'Remove current photo'}
                            </button>
                        )}
                        <button type="button" onClick={closeDialog} disabled={Boolean(editor.busy)} className={`${rowClass} text-[#414141] hover:bg-[#F9F9F9]`}>
                            Cancel
                        </button>
                    </div>
                )}
                <p className="border-t border-[#d9d9d9] bg-[#F9F9F9] px-5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] pt-2.5 text-center text-[11px] font-semibold text-[#414141] sm:pb-2.5">
                    JPG, PNG, WebP, or HEIC · up to 5 MB
                </p>
            </div>
        </div>,
        document.body,
    );
};

export const ProfilePhotoAvatar = ({ editor, profileLoading = false }) => {
    const triggerRef = useRef(null);
    const label = editor.hasPhoto ? editor.copy.changeLabel : editor.copy.addLabel;
    const showSpinner = profileLoading && !editor.avatarUrl;

    return (
        <div className="relative shrink-0">
            <button
                ref={triggerRef}
                type="button"
                onClick={editor.openDialog}
                aria-label={label}
                aria-haspopup="dialog"
                title={label}
                className="group relative block rounded-full outline-none transition focus-visible:ring-4 focus-visible:ring-[#D81F26]/25"
            >
                <DriverAvatar
                    src={editor.avatarUrl}
                    alt={editor.copy.title}
                    glyph={editor.copy.glyph}
                    className="h-16 w-16 ring-0 ring-[#D81F26]/25 transition group-hover:ring-4 group-hover:brightness-95 card-wide:h-[72px] card-wide:w-[72px]"
                    iconSize={24}
                    strokeWidth={2.3}
                >
                    {showSpinner && (
                        <span className="absolute inset-0 flex items-center justify-center bg-black/35" role="status" aria-label="Loading profile">
                            <Spinner className="h-5 w-5 border-white/40 border-t-white" />
                        </span>
                    )}
                </DriverAvatar>
                <span className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-[#414141] text-white shadow-sm transition group-hover:scale-105 group-hover:bg-[#2A2A2A]">
                    <CameraIcon size={12} />
                </span>
            </button>
            <input
                ref={editor.fileInputRef}
                type="file"
                accept={AVATAR_ACCEPT}
                className="hidden"
                onChange={editor.handleFileSelected}
                data-testid="avatar-file-input"
            />
            {editor.dialogOpen && <ProfilePhotoDialog editor={editor} returnFocusRef={triggerRef} />}
        </div>
    );
};

// Brief confirmation under the name after a save/remove; everything else lives in the dialog.
export const ProfilePhotoActions = ({ editor }) => {
    if (!editor.success) return null;
    return (
        <p role="status" className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
            <Icon size={13}>
                <path d="M20 6 9 17l-5-5" />
            </Icon>
            {editor.success}
        </p>
    );
};
